import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Pulse,
  User,
  EnvelopeSimple,
  LockKey,
  Trash,
  Plus,
  ArrowLeft,
  ChatCircleText,
} from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const MAX_CONTACTS = 5;

const SettingsPage = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading, logout } = useAuth();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [ownerName, setOwnerName] = useState("");
  const [pin, setPin] = useState("");
  const [pinSet, setPinSet] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [emergencyMessage, setEmergencyMessage] = useState("");

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/", { replace: true });
    }
  }, [authLoading, user, navigate]);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/user/settings`, { withCredentials: true });
      setOwnerName(res.data.owner_name || "");
      setPinSet(res.data.pin_set);
      setContacts(res.data.emergency_contacts || []);
      setEmergencyMessage(res.data.emergency_message || "");
    } catch (e) {
      console.error(e);
      toast.error("No se pudieron cargar los ajustes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) fetchSettings();
  }, [user]);

  const addContact = () => {
    if (contacts.length >= MAX_CONTACTS) {
      toast.error(`Máximo ${MAX_CONTACTS} contactos`);
      return;
    }
    setContacts([...contacts, { name: "", email: "" }]);
  };

  const updateContact = (idx, field, value) => {
    const next = [...contacts];
    next[idx] = { ...next[idx], [field]: value };
    setContacts(next);
  };

  const removeContact = (idx) => {
    setContacts(contacts.filter((_, i) => i !== idx));
  };

  const handleSave = async () => {
    // Validate
    if (!ownerName.trim()) {
      toast.error("El nombre no puede estar vacío");
      return;
    }
    for (const c of contacts) {
      if (!c.email.includes("@") || !c.name.trim()) {
        toast.error("Cada contacto necesita nombre y email válido");
        return;
      }
    }
    if (pin.trim() && (!/^\d+$/.test(pin.trim()) || pin.trim().length < 4 || pin.trim().length > 8)) {
      toast.error("El PIN debe tener entre 4 y 8 dígitos");
      return;
    }

    setSaving(true);
    try {
      const body = {
        owner_name: ownerName.trim(),
        emergency_contacts: contacts,
        emergency_message: emergencyMessage.trim(),
      };
      // Only send PIN if changed
      if (pin !== "") {
        body.pin = pin.trim();
      }
      await axios.put(`${API}/user/settings`, body, { withCredentials: true });
      toast.success("Ajustes guardados");
      setPin("");
      await fetchSettings();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Error al guardar";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleRemovePin = async () => {
    if (!confirm("¿Quitar el PIN? Cualquier toque del botón gigante registrará un check-in sin confirmación.")) return;
    try {
      await axios.put(
        `${API}/user/settings`,
        { pin: "" },
        { withCredentials: true }
      );
      toast.success("PIN eliminado");
      setPin("");
      await fetchSettings();
    } catch {
      toast.error("Error al eliminar el PIN");
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-white/60 text-sm">
        Cargando ajustes...
      </div>
    );
  }
  if (!user) return null;

  return (
    <div className="min-h-screen w-full text-white" data-testid="settings-page">
      <header className="w-full border-b border-border/60">
        <div className="max-w-3xl mx-auto px-6 md:px-12 py-5 flex items-center justify-between">
          <button
            onClick={() => navigate("/dashboard")}
            className="flex items-center gap-2 text-white/70 hover:text-aqua transition-colors"
            data-testid="back-to-dashboard"
          >
            <ArrowLeft size={18} weight="bold" />
            <span className="text-sm">Dashboard</span>
          </button>
          <div className="flex items-center gap-3">
            <Pulse size={22} weight="duotone" className="text-aqua" />
            <span className="font-heading font-black text-base tracking-[0.4em]">
              AQUÍSIGO
            </span>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 md:px-12 py-10 md:py-14">
        <p className="text-xs uppercase tracking-[0.4em] text-white/50 mb-3">
          Configuración
        </p>
        <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tight leading-[1.05] mb-10">
          Ajustes de tu <span className="text-aqua">cuenta</span>
        </h1>

        {/* Account info */}
        <section className="bg-surface border border-border rounded-2xl p-6 mb-6" data-testid="settings-account">
          <h2 className="font-heading font-bold text-lg tracking-tight mb-4 flex items-center gap-2">
            <User size={18} weight="duotone" className="text-aqua" />
            Cuenta
          </h2>
          <div className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-[0.25em] text-white/55 mb-1.5 block">
                Email (Google)
              </label>
              <div className="mono font-mono text-sm text-white/85 bg-background border border-border rounded-lg px-3 py-2.5">
                {user.email}
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.25em] text-white/55 mb-1.5 block">
                Nombre que aparecerá en las alertas
              </label>
              <Input
                value={ownerName}
                onChange={(e) => setOwnerName(e.target.value)}
                placeholder="Tu nombre"
                className="bg-background border-border text-white"
                data-testid="settings-owner-name"
              />
            </div>
          </div>
        </section>

        {/* PIN */}
        <section className="bg-surface border border-border rounded-2xl p-6 mb-6" data-testid="settings-pin">
          <h2 className="font-heading font-bold text-lg tracking-tight mb-1 flex items-center gap-2">
            <LockKey size={18} weight="duotone" className="text-aqua" />
            PIN de seguridad
          </h2>
          <p className="text-sm text-white/55 mb-4">
            {pinSet
              ? "Tienes un PIN configurado. Escribe uno nuevo para cambiarlo, o elimínalo."
              : "Sin PIN. Cualquier toque del botón registrará un check-in. Configura uno para evitar activaciones accidentales."}
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <Input
              type="password"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={8}
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
              placeholder={pinSet ? "Nuevo PIN (4-8 dígitos)" : "PIN (4-8 dígitos)"}
              className="bg-background border-border text-white max-w-[220px] mono font-mono tracking-widest"
              data-testid="settings-pin-input"
            />
            {pinSet && (
              <Button
                variant="outline"
                onClick={handleRemovePin}
                className="border-danger/50 text-danger hover:bg-danger/10 hover:text-danger"
                data-testid="settings-pin-remove"
              >
                <Trash size={14} className="mr-1.5" />
                Quitar PIN
              </Button>
            )}
          </div>
        </section>

        {/* Contacts */}
        <section className="bg-surface border border-border rounded-2xl p-6 mb-6" data-testid="settings-contacts">
          <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
            <h2 className="font-heading font-bold text-lg tracking-tight flex items-center gap-2">
              <EnvelopeSimple size={18} weight="duotone" className="text-aqua" />
              Contactos de emergencia
              <span className="text-xs font-normal text-white/45 ml-1">
                ({contacts.length}/{MAX_CONTACTS})
              </span>
            </h2>
            <Button
              size="sm"
              onClick={addContact}
              disabled={contacts.length >= MAX_CONTACTS}
              variant="outline"
              className="border-aqua/50 text-aqua hover:bg-aqua/10 hover:text-aqua"
              data-testid="settings-add-contact"
            >
              <Plus size={14} className="mr-1" />
              Añadir
            </Button>
          </div>

          {contacts.length === 0 ? (
            <div className="bg-background border border-dashed border-border rounded-xl p-6 text-center text-white/45 text-sm">
              No tienes contactos. Añade al menos uno para recibir alertas.
            </div>
          ) : (
            <div className="space-y-3">
              {contacts.map((c, i) => (
                <div
                  key={i}
                  className="grid grid-cols-1 sm:grid-cols-[1fr_1.4fr_auto] gap-2 items-center"
                  data-testid={`settings-contact-${i}`}
                >
                  <Input
                    value={c.name}
                    onChange={(e) => updateContact(i, "name", e.target.value)}
                    placeholder="Nombre"
                    className="bg-background border-border text-white"
                  />
                  <Input
                    type="email"
                    value={c.email}
                    onChange={(e) => updateContact(i, "email", e.target.value)}
                    placeholder="email@ejemplo.com"
                    className="bg-background border-border text-white mono font-mono text-sm"
                  />
                  <Button
                    size="icon"
                    variant="outline"
                    onClick={() => removeContact(i)}
                    className="border-border text-white/55 hover:text-danger hover:border-danger/50"
                    data-testid={`settings-contact-remove-${i}`}
                  >
                    <Trash size={16} />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Emergency message */}
        <section className="bg-surface border border-border rounded-2xl p-6 mb-8" data-testid="settings-message">
          <h2 className="font-heading font-bold text-lg tracking-tight mb-1 flex items-center gap-2">
            <ChatCircleText size={18} weight="duotone" className="text-aqua" />
            Mensaje de la alerta
          </h2>
          <p className="text-sm text-white/55 mb-3">
            Texto que verán tus contactos cuando expire el temporizador.
          </p>
          <textarea
            value={emergencyMessage}
            onChange={(e) => setEmergencyMessage(e.target.value)}
            rows={4}
            maxLength={1000}
            className="w-full bg-background border border-border rounded-lg px-3 py-2.5 text-sm text-white resize-none focus:outline-none focus:border-aqua/50 transition-colors"
            data-testid="settings-message-textarea"
          />
          <div className="text-right text-[11px] text-white/35 mono mt-1">
            {emergencyMessage.length} / 1000
          </div>
        </section>

        {/* Action bar */}
        <div className="flex items-center justify-between gap-3 flex-wrap mb-12">
          <Button
            variant="outline"
            onClick={logout}
            className="border-border text-white/70 hover:text-danger hover:border-danger/50"
            data-testid="settings-logout"
          >
            Cerrar sesión
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-aqua text-background hover:bg-aqua/90"
            data-testid="settings-save"
          >
            {saving ? "Guardando..." : "Guardar cambios"}
          </Button>
        </div>
      </main>
    </div>
  );
};

export default SettingsPage;
